import React, { FC } from 'react';
import { IDataTableColumn, IDataTableRow } from '../models';
import { Value } from '@src/atoms/Value';
import { DataTableCellModifier } from '../_CellModifier/DataTableCellModifier';
import { DATA_ELEM_CELL } from '../consts';

import mainStyles from '../DataTable.scss';
import { IDefaultComponentProps } from '@src/models/props';
import { useDataTableContext } from '../context';
import { classnames } from '@src/utils/classnames';

import styles from './DataTableCell.scss';
import { DataTableBodyRowAction } from '../_BodyRow/_Action/DataTableBodyRowAction';
import { propsHooks } from '@src/hooks/propsHooks';
import { Box } from '@src/atoms/Box';
import { SUBROW_CELL_PADDING } from './consts';

interface IDataTableCellProps {
  column: IDataTableColumn;
  row: IDataTableRow;
  isSubRow?: boolean;
  index?: number;
  className?: string;
  setToggle?: (b: boolean) => void;
  nestLevel: number;
}

interface IDataTableCellWrapperProps extends IDefaultComponentProps {
  index?: number;
  isSubRow?: boolean;
  nestLevel?: number;
  hoveredCallbacks?: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
}

export const DataTableCellWrapper: FC<IDataTableCellWrapperProps> = ({
  children,
  index,
  isSubRow,
  hoveredCallbacks,
  nestLevel,
}) => {
  const isFirstSubCell = isSubRow && Number(index) >= 0;
  const padding = Number(nestLevel) * SUBROW_CELL_PADDING;
  const classes = classnames(mainStyles.cell, isSubRow && styles.subRowCell);
  return (
    <div
      className={classes}
      style={{
        paddingLeft: isFirstSubCell ? `${padding}px` : undefined,
      }}
      data-elem={DATA_ELEM_CELL}
      {...hoveredCallbacks}
    >
      {children}
    </div>
  );
};

export const DataTableCell: FC<IDataTableCellProps> = ({ column, row, isSubRow, index, className, nestLevel }) => {
  const { api } = useDataTableContext();
  const { isHovered, hoveredCallbacks } = propsHooks.useHovered();
  const semanticType = column.semanticType;
  const data = row.data[column.id];

  return (
    <DataTableCellWrapper
      index={index}
      isSubRow={isSubRow}
      className={className}
      hoveredCallbacks={hoveredCallbacks}
      nestLevel={nestLevel}
    >
      {data ? (
        <DataTableCellModifier row={row} modifiers={column.cellModifiers}>
          <Box gap={8} alignItems={'center'} justifyContent={'spaceBetween'} className={styles.container}>
            {data.content ? (
              <>{typeof data.content === 'function' ? data.content(row, api) : data.content}</>
            ) : (
              <Value
                value={typeof data.rawValue === 'function' ? data.rawValue(row, api) : data.rawValue}
                semanticType={semanticType}
                size={data.valueSize}
                textAlign={column.align}
              />
            )}
            {data?.actions && (
              <DataTableBodyRowAction rowData={row.data} actions={data.actions} isDisplay={isHovered} />
            )}
          </Box>
        </DataTableCellModifier>
      ) : null}
    </DataTableCellWrapper>
  );
};
